import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Box, Button, FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
} from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'

import { FormLabel } from '../../components/FormLabel'
import { FormSpacer } from '../../components/FormSpacer'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { RadioStack } from '../../components/RadioStack'
import { Add } from '../../icons'
import { InsuranceParamsPropertyDto, Property } from 'BrokerTsLib/src/dtos'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { usePropertyOffersContext } from '../../context/PropertyOffersProvider'
import { useAddVariant } from '../../hooks/useWizard'
import { useState } from 'react'
import { useNameof } from '../../hooks/useNameof'
import { PropertyTypeSelector } from './PropertyInsuranceTypeSelector';
import { scrollToOfferWithOffset } from '../offers/OfferCar';
import { Risk, RiskVariant } from 'BrokerTsLib/src/enums';
import { useInsurers } from '../../hooks';

export const NewInsuranceSettings = () => {
  const nameof = useNameof<Property>()

  const { insuranceParams: insuranceParamsGeneric, insuranceParamsId, refetchInsuranceParams } = useInsuranceParamsContext() ?? {}
  const insuranceParams = insuranceParamsGeneric as InsuranceParamsPropertyDto
  const { refetchOffers: getOffers } = usePropertyOffersContext() ?? {}
  const { mutateAsync: createVariant } = useAddVariant(insuranceParamsId!)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { data: allInsurers, isLoading: isLoadingInsurers, isFetching: isFetchingInsurers } = useInsurers(false, null)

  const methods = useForm<Property>({
    defaultValues: insuranceParams.property,
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = methods

  const onSubmitForm = handleSubmit((values: Property) => {
    setIsSubmitting(true)

    createVariant(values)
      .then(() => {
        refetchInsuranceParams()
        getOffers()
        scrollToOfferWithOffset('steps')
        setIsSubmitting(false)
      })
      .catch(() => {
        setIsSubmitting(false)
      })
  })

  const hasHouseholdAndRealtyVariant = !!insuranceParams.property.variants
    ?.find(x => x.variantParts
      ?.find(x => x.risk == Risk.PROPERTY_TYPE)
      ?.riskVariant == RiskVariant.HouseholdAndRealty
    )

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <Box sx={{ display: 'grid', gap: 4, placeItems: 'start' }}>
          {hasHouseholdAndRealtyVariant &&
            <FormControl isInvalid={!!errors.type}>
              <FormLabel>Typ pojištění</FormLabel>

              <PropertyTypeSelector prefix={nameof(x => x.type)} />

              {errors.type && <FormErrorMessage>{errors.type.message}</FormErrorMessage>}
            </FormControl>
          }

          <FormSpacer />

          <FormControl isInvalid={!!errors.range}>
            <FormLabel>Rozsah pojištění</FormLabel>

            <RadioGroup
              onChange={(x) => setValue('range', parseInt(x))}
              value={watch('range')?.toString()}
            >
              <RadioStack variant="stackedTile">
                <Radio value={RiskVariant.MiniRange.toString()}>Nižší</Radio>
                <Radio value={RiskVariant.MaxiRange.toString()}>Vyšší</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.range && <FormErrorMessage>{errors.range.message}</FormErrorMessage>}
          </FormControl>

          <FormSpacer />

          {/* <InsuranceCompanies companies={chaloupkaCompanies} isLoadingCompanies={false} propPath='insurers' /> */}
          <FormControl isInvalid={!!errors.insurers}>
            <InsuranceCompanies
              companies={(allInsurers?.data ?? [])}
              isLoadingCompanies={(isFetchingInsurers || isLoadingInsurers)}
              propPath={nameof(x => x.insurers)}
              tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění." />

            {errors.insurers && <FormErrorMessage>{errors.insurers.message}</FormErrorMessage>}
          </FormControl>

          <Button type="submit" variant="outline" isLoading={isSubmitting} isDisabled={isSubmitting}>
            <Add color="secondary.500" />
            Přidat do kalkulace
          </Button>
        </Box>
      </form>
    </FormProvider>
  )
}
